import React from 'react';
import { useHistory, useLocation } from 'react-router-dom';
import { SearchOutlined, LoginOutlined, InfoCircleOutlined } from '@ant-design/icons';
import { useQuery } from '@tanstack/react-query';
import debounce from 'lodash.debounce';
import {
  Table, Avatar, Tag, Input, Pagination, Select, Tooltip, Empty,
} from 'antd';
import qs from 'qs';
import PropTypes from 'prop-types';
import {
  countUsers, listUsers, countUsersV2, listUsersV2, resolveUsersV2Filter,
  getWorkspaceModuleContextV2,
} from '../../services/Admin';
import { APP } from '../../services/Constants';
import PlatformLogo from '../../components/PlatformLogo';

const PLATFORM_LABEL = {
  SLACK: 'Slack',
  TEAMS: 'Teams',
  PUMBLE: 'Pumble',
  WEB: 'Web',
};

function Users({ match }) {
  // Pagination
  const location = useLocation();
  const history = useHistory();
  const search = qs.parse(location.search, { ignoreQueryPrefix: true });
  const currentPage = +search.page || 1;
  const onPaginationChange = (page) => {
    const newSearch = {
      ...search,
      page,
    };
    history.push({
      ...location,
      search: qs.stringify(newSearch),
    });
  };
  // Search
  const onSearchChange = (e) => {
    const newSearch = {
      ...search,
      q: e.target.value,
      page: 1,
    };
    history.push({
      ...location,
      search: qs.stringify(newSearch),
    });
  };
  // Data retrieving
  const [, app] = match.path.split('/');
  const isV2 = (APP[app]?.version || 1) >= 2;
  // Filter (defaults differ per version: V1 = ELIGIBLE, V2 = ALL)
  const userFilter = search.f || (isV2 ? 'ALL' : 'ELIGIBLE');
  const onFilterChange = (f) => {
    const newSearch = {
      ...search,
      f,
    };
    history.push({
      ...location,
      search: qs.stringify(newSearch),
    });
  };
  const scopeId = isV2 ? search.workspaceId : search.teamId;
  // V2 requires a workspaceId to scope the query — otherwise we'd list every
  // User across the whole app, which is huge. Render an empty state instead.
  const needsScope = isV2 && !scopeId;
  // V2: load workspace + module context (admins + integration ids for scoping)
  const { data: context } = useQuery(
    ['wsModuleContext', app, scopeId],
    () => getWorkspaceModuleContextV2(app, scopeId),
    { enabled: isV2 && !!scopeId, cacheTime: 0 },
  );
  const moduleAdminIdsArr = React.useMemo(
    () => (context?.module?.adminIds || []).map(String),
    [context],
  );
  const moduleAdminIds = React.useMemo(
    () => new Set(moduleAdminIdsArr),
    [moduleAdminIdsArr],
  );
  const moduleIntegrationIds = context?.moduleIntegrationIds || null;
  const integrationsKey = (moduleIntegrationIds || []).map(String).sort().join(',');
  const adminsKey = moduleAdminIdsArr.slice().sort().join(',');
  // V2 resolves its User filter as its own query so the list and count share a
  // single resolution instead of each running the IntegrationUser search, and so
  // paging reuses it. Keyed without currentPage for exactly that reason.
  const { data: v2Filter } = useQuery(
    ['usersV2Filter', app, userFilter, scopeId, search.q, integrationsKey, adminsKey],
    () => resolveUsersV2Filter(app, userFilter, scopeId, search.q, {
      scopeIntegrationIds: moduleIntegrationIds,
      moduleAdminIds: moduleAdminIdsArr,
    }),
    { cacheTime: 0, enabled: isV2 && !needsScope && !!context },
  );
  const isReady = !needsScope && (isV2 ? !!v2Filter : true);
  const { isLoading, data: users } = useQuery(
    ['listUsers', app, userFilter, currentPage, scopeId, search.q, integrationsKey, adminsKey],
    () => (isV2
      ? listUsersV2(app, v2Filter, currentPage, moduleIntegrationIds)
      : listUsers(app, userFilter, currentPage, scopeId, search.q)),
    { cacheTime: 0, enabled: isReady },
  );
  const { data: count, isLoading: isCountLoading } = useQuery(
    ['countUsers', app, userFilter, scopeId, search.q, adminsKey, integrationsKey],
    () => (isV2
      ? countUsersV2(app, v2Filter)
      : countUsers(app, userFilter, scopeId, search.q)),
    { cacheTime: 0, enabled: isReady },
  );
  if (needsScope) {
    return (
      <div id="Users">
        <Empty
          style={{ marginTop: 80 }}
          description={(
            <span>
              {'Open the '}
              <a href={`/#/${app}/workspaces`}>Workspaces</a>
              {' tab and click '}
              <strong>View users</strong>
              {' on a workspace to see its users here.'}
            </span>
          )}
        />
      </div>
    );
  }
  return (
    <div id="Users">
      <div className="Filters">
        <Select
          value={userFilter}
          style={{ width: 150, marginRight: 8 }}
          onChange={onFilterChange}
        >
          {isV2 ? (
            <>
              <Select.Option value="ALL">All</Select.Option>
              <Select.Option value="ACTIVE">Active</Select.Option>
              <Select.Option value="DEACTIVATED">Deactivated</Select.Option>
              <Select.Option value="ADMIN">Admins</Select.Option>
            </>
          ) : (
            <>
              <Select.Option value="ALL">All</Select.Option>
              <Select.Option value="ELIGIBLE">Eligible only</Select.Option>
              <Select.Option value="BOT">Bot only</Select.Option>
            </>
          )}
        </Select>
        <Input
          prefix={<SearchOutlined />}
          placeholder={isV2 ? 'Search name, email, id…' : 'Search in name, email and userId'}
          defaultValue={search.q || ''}
          style={{ width: 260 }}
          onChange={debounce(onSearchChange, 500)}
          allowClear
        />
        {isV2 && (
          <Tooltip
            title={(
              <div style={{ lineHeight: 1.5 }}>
                <div>
                  <strong>Exact match</strong>
                  :
                </div>
                <div>• user id (24-hex)</div>
                <div>• platform user id (e.g. Slack U…)</div>
                <div style={{ marginTop: 6 }}>
                  <strong>Partial match</strong>
                  {' (case-insensitive):'}
                </div>
                <div>• email</div>
                <div>• integration name (real_name, displayName, …)</div>
                <div>• integration job title</div>
              </div>
            )}
          >
            <InfoCircleOutlined style={{ marginLeft: 8, color: '#888', cursor: 'help' }} />
          </Tooltip>
        )}
        <div style={{ flex: 1 }} />
        <Pagination
          current={currentPage}
          onChange={onPaginationChange}
          total={count}
          pageSize={20}
          showSizeChanger={false}
        />
      </div>
      <Table
        dataSource={users}
        loading={isLoading}
        pagination={false}
        rowKey="_id"
        size="small"
        scroll={{ x: 1500 }}
      >
        <Table.Column
          title={`User ID  (${isCountLoading ? '...' : count})`}
          dataIndex={isV2 ? '_id' : 'userId'}
          key="_id"
        />
        {isV2 ? (
          <>
            <Table.Column
              title=""
              key="_id"
              render={(_, user) => {
                if (!user.loginLink) return '-';
                return (
                  <a href={user.loginLink} target="_blank" rel="noreferrer">
                    <LoginOutlined />
                  </a>
                );
              }}
            />
            <Table.Column
              title="Name"
              key="_id"
              render={(_, user) => {
                const rp = user.resolvedProfile;
                const fn = rp?.firstName ?? user.profile?.firstName ?? '';
                const ln = rp?.lastName ?? user.profile?.lastName ?? '';
                const fullName = rp?.displayName
                  || `${fn} ${ln}`.trim()
                  || user.email;
                const pic = rp?.profilePictureUrl || user.profile?.profilePictureUrl;
                const isModuleAdmin = moduleAdminIds.has(String(user._id));
                return (
                  <span
                    style={{
                      display: 'inline-flex',
                      alignItems: 'center',
                      verticalAlign: 'middle',
                      gap: 6,
                      lineHeight: 1,
                      opacity: user.deactivatedAt ? 0.5 : 1,
                    }}
                  >
                    {pic && <Avatar src={pic} size={14} />}
                    <span>{fullName}</span>
                    {isModuleAdmin && (
                      <Tag
                        color="purple"
                        style={{
                          lineHeight: '16px',
                          padding: '0 4px',
                          fontSize: 11,
                          margin: 0,
                        }}
                      >
                        MOD ADMIN
                      </Tag>
                    )}
                    {user.deactivatedAt && <span>☠️</span>}
                  </span>
                );
              }}
            />
            <Table.Column
              title="Email"
              dataIndex="email"
              key="_id"
            />
            <Table.Column
              title="Role"
              key="_id"
              render={(_, user) => (
                <Tag
                  color={user.role === 'ADMIN' ? 'red' : 'default'}
                  style={{
                    lineHeight: '16px',
                    padding: '0 4px',
                    fontSize: 11,
                    margin: 0,
                  }}
                >
                  {user.role || 'USER'}
                </Tag>
              )}
            />
            <Table.Column
              title="Job Title"
              key="_id"
              render={(_, user) => user.resolvedProfile?.jobTitle || user.profile?.jobTitle || '-'}
            />
            <Table.Column
              title="Timezone"
              key="_id"
              render={(_, user) => user.resolvedProfile?.tz || user.profile?.tz || '-'}
            />
            <Table.Column
              title="Status"
              key="_id"
              render={(_, user) => (
                <Tag
                  color={user.deactivatedAt ? 'default' : 'green'}
                  style={{
                    lineHeight: '16px',
                    padding: '0 4px',
                    fontSize: 11,
                    margin: 0,
                  }}
                >
                  {user.deactivatedAt ? 'Deactivated' : 'Active'}
                </Tag>
              )}
            />
            <Table.Column
              title="Int."
              key="_id"
              render={(_, user) => {
                const platforms = user.platforms || [];
                if (!platforms.length) return '-';
                return (
                  <div style={{ whiteSpace: 'nowrap' }}>
                    {platforms.map((p, idx) => (
                      <Tooltip
                        // eslint-disable-next-line react/no-array-index-key
                        key={`${p.type}-${p.platformUserId || idx}`}
                        title={(
                          <div>
                            <div>{`${PLATFORM_LABEL[p.type] || p.type}${p.platformTeamId ? ` · ${p.platformTeamId}` : ''}`}</div>
                            {p.email && <div>{p.email}</div>}
                            {p.deactivatedAt && <div>Deactivated</div>}
                          </div>
                        )}
                      >
                        <span
                          style={{
                            display: 'inline-flex',
                            alignItems: 'center',
                            verticalAlign: 'middle',
                            gap: 4,
                            marginRight: 10,
                            lineHeight: 1,
                            opacity: p.deactivatedAt ? 0.4 : 1,
                            filter: p.deactivatedAt ? 'grayscale(1)' : 'none',
                          }}
                        >
                          <PlatformLogo type={p.type} size={12} />
                          <span>{p.platformUserId || '?'}</span>
                        </span>
                      </Tooltip>
                    ))}
                  </div>
                );
              }}
            />
          </>
        ) : (
          <>
            <Table.Column
              title="teamId"
              dataIndex="teamId"
              key="_id"
              render={(teamId) => (
                <a href={`/#/${app}/workspaces?q=${teamId}`}>{teamId}</a>
              )}
            />
            <Table.Column
              title="name"
              dataIndex="_id"
              key="_id"
              render={(_, user) => (
                <span>
                  {user.data.profile.image_48 && (
                    <Avatar
                      src={user.data.profile.image_48}
                      size={18}
                      style={{ marginRight: '8px' }}
                    />
                  )}
                  {user.data.real_name || user.data.profile.real_name}
                  {user.data.is_admin && <Tag color="red" style={{ marginLeft: '8px' }}>ADMIN</Tag>}
                  {user.data.is_bot && <Tag color="blue" style={{ marginLeft: '8px' }}>BOT</Tag>}
                  {user.data.deleted && ' ☠️'}
                </span>
              )}
            />
            <Table.Column
              title="Email"
              dataIndex="_id"
              key="_id"
              render={(_, user) => user.data.profile.email}
            />
            <Table.Column
              title="Job Title"
              dataIndex="_id"
              key="_id"
              render={(_, user) => user.data.profile.title}
            />
            <Table.Column
              title="Timezone"
              dataIndex="_id"
              key="_id"
              render={(_, user) => user.data.tz}
            />
          </>
        )}
      </Table>
    </div>
  );
}

Users.propTypes = {
  // eslint-disable-next-line react/forbid-prop-types
  match: PropTypes.object.isRequired,
};

export default Users;
